import { useEffect, useState } from 'react';

export type LifecycleState =
  | 'initializing'
  | 'inactive'
  | 'foreground'
  | 'background'
  | 'suspended';

type LifecycleListener = (state: LifecycleState, previous: LifecycleState) => void;

let globalLifecycleState: LifecycleState = 'initializing';
let everForegrounded = false;
const lifecycleSubscribers = new Set<LifecycleListener>();

export function getLifecycleState(): LifecycleState {
  return globalLifecycleState;
}

/** True once the app has been in the foreground at least once (i.e. past the initial cold-boot). */
export function hasAppBeenForegrounded(): boolean {
  return everForegrounded;
}

/** Called from the platform system module (e.g. Firebolt) whenever the lifecycle state changes. */
export function updateLifecycleState(state: LifecycleState): void {
  const previousLifecycleState = globalLifecycleState;
  if (state === previousLifecycleState) return;
  globalLifecycleState = state;
  if (state === 'foreground') everForegrounded = true;
  for (const subscriber of lifecycleSubscribers)
    subscriber(state, previousLifecycleState);
}

/** React hook that re-renders when the app lifecycle state changes. */
export function useAppLifecycle(): LifecycleState {
  const [localLifecycleState, setLocalLifecycleState] =
    useState<LifecycleState>(globalLifecycleState);

  useEffect(() => {
    // Sync in case state changed between render and effect registration.
    setLocalLifecycleState(globalLifecycleState);
    const lifecycleSubscriber: LifecycleListener = (nextLifecycleState) =>
      setLocalLifecycleState(nextLifecycleState);
    lifecycleSubscribers.add(lifecycleSubscriber);
    return () => {
      lifecycleSubscribers.delete(lifecycleSubscriber);
    };
  }, []);

  return localLifecycleState;
}
